import { LocalDate, LocalDateTime, LocalTime, ZoneId, Instant } from '@js-joda/core';

/**
 * Utility for converting dates between representations.
 */
export class DateConversion {
    static localDateToStartOfDayJsUTC(ld: LocalDate): Date {
        if (ld) {
            let instant = ld.atStartOfDay(ZoneId.UTC).toInstant();
            let date = new Date(instant.toEpochMilli());
            return date;
        }
        else {
            return undefined;
        }
    }

    static localDateToEndOfDayJsUTC(ld: LocalDate): Date {
        if (ld) {
            let instant = ld.atTime(LocalTime.MAX).atZone(ZoneId.UTC).toInstant();
            return new Date(instant.toEpochMilli());
        }
        else {
            return undefined;
        }
    }

    static localDateToStartOfDayJsLocal(ld: LocalDate): Date {
        if (ld) {
            let instant = ld.atStartOfDay(ZoneId.SYSTEM).toInstant();
            let date = new Date(instant.toEpochMilli());
            return date;
        }
        else {
            return undefined;
        }
    }

    static localDateToEndOfDayJsLocal(ld: LocalDate): Date {
        if (ld) {
            let instant = ld.atTime(LocalTime.MAX).atZone(ZoneId.SYSTEM).toInstant();
            return new Date(instant.toEpochMilli());
        }
        else {
            return undefined;
        }
    }

    static jsToLocalDate(dt: Date): LocalDate {
        if (dt) {
            let newInstant =  Instant.ofEpochMilli(dt.getTime());

            let newDate = LocalDateTime.ofInstant(newInstant, ZoneId.SYSTEM).toLocalDate();

            return newDate;
        }
        else
            return undefined;
    }
}

/**
 * Define a range of dates (no times).
 * Specified without time zones.
 */
export class DateRange {
    constructor(
        /** Date to start the range, as Joda LocalDate. */
        public startDate?: LocalDate,
        /** Inclusive date to end the range, as Joda LocalDate. */
        public endDate?: LocalDate
    ) {
    }

    toString(): string {
        return JSON.stringify(this);
    }

    /** When to start the range (always beginning of day on startDate), as Joda LocalDateTime. */
    get startDateTime(): LocalDateTime {
        return this.startDate ? this.startDate.atStartOfDay() : undefined;
    }

    /** Inclusive end of the range (always the last nanosecond of endDate), as Joda LocalDateTime. */
    get endDateTime(): LocalDateTime {
        return this.endDate ? this.endDate.atTime(LocalTime.MAX).withNano(0) : undefined;
    }

    /** When to start the range (always beginning of day on startDate), as an ISO 8601 string. */
    get startDateTimeIsoString(): string {
        return this.startDate ? this.startDateTime.toString() : undefined;
    }

    /** Inclusive end of the range (always the last nanosecond of endDate), as an ISO 8601 string. */
    get endDateTimeIsoString(): string {
        return this.endDate ? this.endDateTime.toString() : undefined;
    }

    /** Set when to start the range (will truncate to beginning of day), as a Javascript Date. */
    set startJsDateUTC(dt: Date) {
        this.startDate = dt ? DateConversion.jsToLocalDate(dt) : undefined;
    }

    /** When to start the range (always beginning of day on startDate), as a Javascript Date. */
    get startJsDateUTC(): Date {
        return DateConversion.localDateToStartOfDayJsUTC(this.startDate);
    }

    /** When to start the range (always beginning of day on startDate), as a Javascript Date. */
    get startJsDateLocal(): Date {
        return DateConversion.localDateToStartOfDayJsLocal(this.startDate);
    }

    /** Inclusive end of the range (will adjust to last millisecond of date), as Javascript Date */
    set endJsDateUTC(dt: Date) {
        this.endDate = dt ? DateConversion.jsToLocalDate(dt) : undefined;
    }

    /** Inclusive end of the range (always the last millisecond of endDate), as Javascript Date */
    get endJsDateUTC(): Date {
        return DateConversion.localDateToEndOfDayJsUTC(this.endDate);
    }

    /** Inclusive end of the range (always the last millisecond of endDate), as Javascript Date */
    get endJsDateLocal(): Date {
        return DateConversion.localDateToEndOfDayJsLocal(this.endDate);
    }

    /** Inclusive end of the range (always the last millisecond of endDate), as Javascript Date */


    /** Does this DateRange match other? */
    isEqual(other: DateRange): boolean {
        return (this.startDate || LocalDate.MAX).isEqual(other.startDate || LocalDate.MAX) &&
            (this.endDate || LocalDate.MAX).isEqual(other.endDate || LocalDate.MAX);
    }

    /** Are the start and end dates both set? */
    isDefined() {
        return this.startDate && this.endDate;
    }
}

export type PresetDateRangeCategory = 'calendar'|'lastx'|'today'|'pharmacy-charges'|'invoice-approval'|'delivery-manifests';

/**
 * Define a preset date range for the user to select.
 */
export abstract class PresetDateRange {

    /** Position in the PresetDateRanges array */
    index: number;

    /** Is it a calendar-based range, or a "Last x Days" style? */
    category: PresetDateRangeCategory;

    /** Display text */
    display: string;

    /** Return the current DateRange that meets this preset's criteria. */
    abstract getRange(): DateRange;
}


/**
 * List of presets, in display order.
 */
export const PresetDateRanges: PresetDateRange[] = [
    {
        index: 0,
        category: 'calendar',
        display: "This Month",
        getRange: ((): DateRange => {
            let startThisMonth = LocalDate.now(ZoneId.SYSTEM).withDayOfMonth(1);
            return new DateRange(
                startThisMonth,
                startThisMonth.plusMonths(1).minusDays(1));
        })
    },
    {
        index: 1,
        category: 'calendar',
        display: "Last Month",
        getRange: ((): DateRange => {
            let startThisMonth = LocalDate.now(ZoneId.SYSTEM).withDayOfMonth(1);
            return new DateRange(
                startThisMonth.minusMonths(1),
                startThisMonth.minusDays(1));
        })
    },
    {
        index: 2,
        category: 'calendar',
        display: "This Quarter",
        getRange: ((): DateRange => {
            let start = LocalDate.now(ZoneId.SYSTEM).withDayOfMonth(1);
            start = start.withMonth(start.month().firstMonthOfQuarter());
            return new DateRange(start, start.plusMonths(3).minusDays(1));
        })
    },
    {
        index: 3,
        category: 'calendar',
        display: "Last Quarter",
        getRange: ((): DateRange => {
            let start = LocalDate.now(ZoneId.SYSTEM).minusMonths(3).withDayOfMonth(1);
            start = start.withMonth(start.month().firstMonthOfQuarter());
            return new DateRange(start, start.plusMonths(3).minusDays(1));
        })
    },
    {
        index: 4,
        category: 'calendar',
        display: "This Year",
        getRange: ((): DateRange => {
            let start = LocalDate.now(ZoneId.SYSTEM).withMonth(1).withDayOfMonth(1);
            return new DateRange(start, start.plusYears(1).minusDays(1));
        })
    },
    {
        index: 5,
        category: 'calendar',
        display: "Last Year",
        getRange: ((): DateRange => {
            let day = LocalDate.now(ZoneId.SYSTEM).withMonth(1).withDayOfMonth(1);
            return new DateRange(day.minusYears(1), day.minusDays(1));
        })
    },
    {
        index: 6,
        category: 'lastx',
        display: 'Last 30 Days',
        getRange: ((): DateRange => {
            let today = LocalDate.now(ZoneId.SYSTEM);
            return new DateRange(today.minusDays(30), today);
        })
    },
    {
        index: 7,
        category: 'lastx',
        display: 'Last 90 Days',
        getRange: ((): DateRange => {
            let today = LocalDate.now(ZoneId.SYSTEM);
            return new DateRange(today.minusDays(90), today);
        })
    },
    {
        index: 8,
        category: 'lastx',
        display: 'Last 6 Months',
        getRange: ((): DateRange => {
            let today = LocalDate.now(ZoneId.SYSTEM);
            return new DateRange(today.minusMonths(6), today);
        })
    },
    {
        index: 9,
        category: 'today',
        display: 'Today',
        getRange: ((): DateRange => {
            let today = LocalDate.now(ZoneId.SYSTEM);
            return new DateRange(today, today);
        })
    },
    // Copy of the calendar list with missing elements. Remove once all options can be enabled
    {
        index: 10,
        category: 'pharmacy-charges',
        display: "This Month",
        getRange: ((): DateRange => {
            let startThisMonth = LocalDate.now(ZoneId.SYSTEM).withDayOfMonth(1);
            return new DateRange(startThisMonth, startThisMonth.plusMonths(1).minusDays(1));

        })
    },
    {
        index: 11,
        category: 'pharmacy-charges',
        display: "Last Month",
        getRange: ((): DateRange => {
            let startThisMonth = LocalDate.now(ZoneId.SYSTEM).withDayOfMonth(1);
            return  new DateRange(
                startThisMonth.minusMonths(1),
                startThisMonth.minusDays(1));

        })
    },
    {
        index: 12,
        category: 'pharmacy-charges',
        display: "This Quarter",
        getRange: ((): DateRange => {
            let start = LocalDate.now(ZoneId.SYSTEM).withDayOfMonth(1);
            start = start.withMonth(start.month().firstMonthOfQuarter());
            return new DateRange(start, start.plusMonths(3).minusDays(1));
        })
    },
    {
        index: 13,
        category: 'pharmacy-charges',
        display: "Last Quarter",
        getRange: ((): DateRange => {
            let start = LocalDate.now(ZoneId.SYSTEM).minusMonths(3).withDayOfMonth(1);
            start = start.withMonth(start.month().firstMonthOfQuarter());
            return new DateRange(start, start.plusMonths(3).minusDays(1));
        })
    },
    {
        index: 14,
        category: 'invoice-approval',
        display: 'Last 6 Months',
        getRange: ((): DateRange => {
            let today = LocalDate.now(ZoneId.SYSTEM);
            return new DateRange(today.minusMonths(6), today);
        })
    },
    {
        index: 15,
        category: 'invoice-approval',
        display: "This Year",
        getRange: ((): DateRange => {
            let start = LocalDate.now(ZoneId.SYSTEM).withMonth(1).withDayOfMonth(1);
            return new DateRange(start, start.plusYears(1).minusDays(1));
        })
    },
    {
        index: 16,
        category: 'invoice-approval',
        display: "Last Year",
        getRange: ((): DateRange => {
            let day = LocalDate.now(ZoneId.SYSTEM).withMonth(1).withDayOfMonth(1);
            return new DateRange(day.minusYears(1), day.minusDays(1));
        })
    },
    {
        // TODO: This should belong to the lastx category, but it'd be nice to be
        // able to filter inside of the preset date range selector component (e.g only
        // use a single one out of an entire category)
        index: 17,
        category: 'delivery-manifests',
        display: 'Last 3 Days',
        getRange: ((): DateRange => {
            let today = LocalDate.now(ZoneId.SYSTEM);
            return new DateRange(today.minusDays(3), today);
        })
    },
];
